import { useMemo, useState } from 'react';
import { PlansBillingController } from '../../../controllers';
import constant from '../../../utils/constant';
import moment from 'moment';
import { amountFormatter, copyAction } from '../../../utils/utils';
import { MimaOptionMenu } from '../../../components';

export const usePlanBilling = () => {
	const switchMenu = ['All Plans', 'All Subscriptions'];

	const [currentSwitch, setCurrentSwitch] = useState(switchMenu[0]);
	const [openModal, setOpenModal] = useState('');
	const [currentPlan, setCurrentPlan] = useState({});
	const [customerId, setCustomerId] = useState('');

	const closeModal = () => {
		setOpenModal('');
		setCurrentPlan({});
		setCustomerId('');
	};

	const openModalHandler = modal => {
		setOpenModal(modal);
	};

	const modalConstants = {
		createPlan: 'CREATE-PLAN',
		viewPlan: 'VIEW-PLAN',
		viewSubscriptionDetails: 'VIEW-SUBSCRIPTION-DETAILS',
	};

	const {
		isLoading,
		isFetching,
		currentPage,
		setCurrentPage,
		setSearchQuery,
		limit,
		setLimit,
		data,
	} = PlansBillingController.useGetPlans();

	const onLimitChange = limit => {
		setLimit(limit);
		setCurrentPage(0);
	};

	const tableData = useMemo(() => {
		if (data?.status === constant.Success) {
			return data?.data?.value || [];
		}
		return [];
	}, [data]);

	const totalCounts = useMemo(() => {
		if (data?.status === constant.Success) {
			return data?.data?.totalCounts;
		}
		return 0;
	}, [data]);

	const tableColumns = [
		{
			Header: 'Plan Name',
			accessor: 'name',
		},
		{
			Header: 'Plan ID',
			accessor: '_id',
		},
		{
			Header: 'Creation Date',
			accessor: 'createdAt',
			Cell: ({ cell }) => {
				const createdAt = cell.row.original.createdAt;

				return <span>{moment(createdAt).format('DD-MM-YYYY')}</span>;
			},
		},
		{
			Header: 'Interval',
			accessor: 'interval',
		},
		{
			Header: 'Price',
			accessor: 'amount',
			Cell: ({ cell }) => {
				const amount = cell.row.original.amount;
				const currencyCode = cell.row.original.currencyCode;

				return <span>{amountFormatter(currencyCode).format(amount)}</span>;
			},
		},
		{
			Header: 'Trial Period',
			accessor: 'trialPeriod',
		},
		{
			Header: 'Status',
			accessor: 'status',
		},
		{
			Header: 'Actions',
			Cell: ({ cell }) => {
				const row = cell.row.original;
				return (
					<MimaOptionMenu
						options={[
							{
								value: 'copy',
								label: 'Copy Plan Id',
							},
							{
								value: 'edit',
								label: 'Edit Plan',
							},
							{
								value: 'view',
								label: 'View Plan',
							},
						]}
						onOptionSelected={option => {
							handleOptionSelect(row, option);
						}}
					/>
				);
			},
		},
	];

	const handleOptionSelect = (row, option) => {
		if (option.value === 'copy') {
			copyAction(row._id);
		} else if (option.value === 'edit') {
			openModalHandler(modalConstants.createPlan);
			setCurrentPlan(row);
		}
		if (option.value === 'viewSub') {
			openModalHandler(modalConstants.viewSubscriptionDetails);
			setCustomerId(row?.customer?._id);
		}
		if (option.value === 'view') {
			openModalHandler(modalConstants.viewPlan);
			setCurrentPlan(row);
		}
	};

	return {
		switchMenu,
		currentSwitch,
		setCurrentSwitch,
		openModal,
		openModalHandler,
		modalConstants,
		closeModal,
		onLimitChange,
		isLoading,
		isFetching,
		currentPage,
		setCurrentPage,
		setSearchQuery,
		limit,
		setLimit,
		tableData,
		tableColumns,
		totalCounts,
		currentPlan,
		customerId,
		handleOptionSelect,
	};
};

export default usePlanBilling;
